import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Stack,
  Chip,
  Divider,
  Avatar,
  useMediaQuery,
  useTheme,
  Button,
  Switch,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { Coverage } from "fhir/r5";

interface InsuranceCardProps {
  coverage: Coverage;
  onEdit: () => void;
  onDelete: () => void;
}

export function InsuranceCard({
  coverage,
  onEdit,
  onDelete,
}: InsuranceCardProps) {
  // Extrair informações do coverage
  const cardNumber = coverage.identifier?.[0]?.value || "";
  const insurerName = coverage.insurer?.display || "";
  const planName = coverage.insurancePlan?.display || "";
  const status = coverage.status || "active";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <Card
      sx={{
        width: "100%",
        border: "1px solid",
        borderColor: "neutral300.main",
        borderRadius: "16px",
        overflow: "visible",
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.1)",
        }
      }}
    >
      <CardContent sx={{ p: 2, width: "100%" }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          justifyContent="space-between"
          mb={2}
        >
          {!isMobile && (
            <Avatar
              sx={{
                bgcolor: "neutral500.main",
                width: 48,
                height: 48,
                fontSize: isMobile ? "1.4rem" : "1.2rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {insurerName ? (
                getInitials(insurerName)
              ) : (
                <HealthAndSafetyOutlinedIcon />
              )}
            </Avatar>
          )}

          <Stack sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight="500">
              {insurerName}
            </Typography>

            <Typography variant="body1" color="neutral700.main">
              {planName}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
          >
            <Tooltip title={status === "active" ? "Desativar" : "Ativar"}>
              <Switch checked={status === "active"} onChange={onDelete} />
            </Tooltip>
          </Stack>
        </Stack>

        <Divider sx={{ width: "100%" }} />
        <Stack
          mt={2}
          direction={isMobile ? "column" : "row"}
          justifyContent={isMobile ? "center" : "space-between"}
          alignItems={isMobile ? "flex-end" : "center"}
          spacing={2}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent={isMobile ? "center" : "flex-start"}
            mt={1}
            sx={{
              width: "100%",
            }}
          >
            <CreditCardIcon color="primary" />
            <Stack spacing={0.5} flex={1} alignItems="flex-start">
              <Typography
                variant="body2"
                color="neutral700.main"
                textAlign={isMobile ? "center" : "left"}
              >
                Número da Carteirinha
              </Typography>
              <Typography
                variant="body1"
                fontWeight={500}
                color="neutral1000.main"
                textAlign={isMobile ? "center" : "left"}
                sx={{
                  letterSpacing: "0.5px",
                  fontSize: isMobile ? "1rem" : "inherit",
                  wordBreak: "break-all",
                }}
              >
                {cardNumber}
              </Typography>
            </Stack>
          </Stack>

          {isMobile && <Divider sx={{ width: "100%" }} />}
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditIcon fontSize="small" />}
            onClick={onEdit}
          >
            Editar
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
